import React, { useState } from "react";
import { Controller, FormState } from "react-hook-form";
import type { Control } from "react-hook-form";
import { Button, Grid, Typography, IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

// Fonction pour accéder aux erreurs imbriquées (similaire à FormTextField)
const getNestedError = (errors: any, name: string) => {
    const keys = name.replace(/\.\[/g, '.').replace(/\]/g, '').split('.');
    let current = errors;
    for (const key of keys) {
        if (!current || !current[key]) {
            return undefined;
        }
        current = current[key];
    }
    return current.message;
};

export const FormFileField = ({
    label,
    name,
    control,
    disabled,
    formState,
    required,
    helperText,
    accept = "audio/*"
}: {
    label: string,
    name: string,
    control: Control,
    disabled?: boolean,
    formState: FormState<any>,
    required?: boolean,
    helperText?: string,
    accept?: string
}) => {
    // Récupérer le message d'erreur pour ce champ
    const errorMessage = getNestedError(formState.errors, name);
    const [fileName, setFileName] = useState<string>('');

    return (
        <Grid item container style={{ marginTop: '15px' }}>
            <Grid item xs={2}>
                {label} {required && <span style={{ color: 'red' }}>*</span>}
            </Grid>
            <Grid item xs={10} style={{ paddingRight: '20px' }}>
                <Controller
                    control={control}
                    name={name}
                    defaultValue=""
                    render={(field, state) => {
                        const { value, onChange: onChangeField, ref } = field;
                        const { invalid } = state;

                        // Fonction pour gérer l'upload de fichier
                        const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                            const files = event.target.files;
                            if (files && files.length > 0) {
                                const file = files[0];
                                setFileName(file.name);

                                // Au lieu de stocker l'objet File directement, on stocke ses propriétés essentielles
                                // ainsi que l'objet lui-même pour pouvoir l'utiliser plus tard
                                onChangeField({
                                    name: file.name,
                                    size: file.size,
                                    type: file.type,
                                    lastModified: file.lastModified,
                                    // On ne peut pas sérialiser le File directement, mais on le garde dans une propriété
                                    // Cette propriété sera ignorée lors de la sérialisation JSON mais toujours disponible
                                    // dans le contexte du composant
                                    _file: file
                                });
                            }
                        };

                        // Fonction pour supprimer le fichier
                        const handleRemoveFile = () => {
                            setFileName('');
                            onChangeField("");
                        };

                        return (
                            <>
                                <input
                                    accept={accept}
                                    style={{ display: 'none' }}
                                    id={`file-input-${name}`}
                                    type="file"
                                    onChange={handleFileChange}
                                    ref={ref}
                                    disabled={disabled}
                                />
                                <label htmlFor={`file-input-${name}`}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        component="span"
                                        startIcon={<CloudUploadIcon />}
                                        disabled={disabled}
                                    >
                                        Sélectionner un fichier
                                    </Button>
                                </label>
                                {(value && fileName) && (
                                    <Grid container alignItems="center" style={{ marginTop: '8px' }}>
                                        <Grid item xs>
                                            <Typography variant="body2">
                                                {typeof value === 'object' && value.name ? value.name : fileName}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <IconButton size="small" onClick={handleRemoveFile}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                )}
                                {errorMessage && (
                                    <Typography color="error" variant="caption" style={{ display: 'block', marginTop: '4px' }}>
                                        {errorMessage}
                                    </Typography>
                                )}
                            </>
                        );
                    }}
                />
                {!errorMessage && helperText && (
                    <Typography variant="caption" color="textSecondary">
                        {helperText}
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
}; 